import React from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import first from 'lodash/first';
import isEqual from 'lodash/isEqual';
import dayjs from '../../../dayjs/dayjs_timezone';
import Alert from '../../components/Alert';
import PaymentOptions from '../components/PaymentOptions';
import Price from '../components/Price';
import TimeSlotRadioButtons from '../components/TimeSlot/TimeSlotRadioButtons';
import ActivityClassPageParent from '../ActivityClassPageParent';
import Translation from '../../utilities/localizationUtility';
import CloseModalIcon from '../components/CloseModalIcon';
import { dateFromParams, getTimeFrameFromDate } from '../../utilities/timeUtility';
import { stringToBoolean } from '../../utilities/conversionUtility';
import { SHORT_DATE_FORMAT } from '../../constants';
import { isHiddenVariant } from '../../utilities/helperUtility';
import UpdatedBookingPage from '../components/UpdatedBookingPage';
import { parseAvailableDates } from '../helpers';
import ConfirmationPage from '../components/ClassConfirmationPage';
import { StyledWrapperRadioList, StyledTimeZone } from './ClassPage.style';
import BrandingString from '../components/BrandingString';

export default class ClassPage extends ActivityClassPageParent {
  constructor(props) {
    super(props);

    this.state = {
      currentMonth: null,
      availableDates: [],
      width: window.innerWidth,
      modalHeight: 100,
      isModalOpen: false,
      messages: [],
    };

    this.handleDayClick = this.handleDayClick.bind(this);
    this.handleDayMouseEnter = this.handleDayMouseEnter.bind(this);
    this.handleMonthChange = this.handleMonthChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleNextMonthClick = this.handleNextMonthClick.bind(this);
    this.onSelectTimeSlot = this.onSelectTimeSlot.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(oldProps) {
    const {
      frames,
      loading,
      quantity,
      selectedLocation: nextSelectedLocation,
      selectedResource: nextSelectedResource,
      selectedVariant: nextSelectedVariant,
      selectedTimeslot,
      timeZone,
      orderActions: { setOrderDetails },
      frameActions: { fetchFrames, resetFirstOccurrence },
      exceededBookingsLimit,
      exceededTrialPlan,
      variants,
    } = this.props;
    const {
      selectedLocation: prevSelectedLocation,
      selectedResource: prevSelectedResource,
      selectedVariant: prevSelectedVariant,
      timeZone: prevTimeZone,
      frames: prevFrames,
    } = oldProps;

    const self = this;
    const { currentMonth } = this.state;
    const isLocationChanged = !isEqual(nextSelectedLocation, prevSelectedLocation);
    const isResourceChanged = !isEqual(nextSelectedResource, prevSelectedResource);
    const varianthanged = !isEqual(nextSelectedVariant, prevSelectedVariant);

    if (isLocationChanged || varianthanged || isResourceChanged) {
      const variant = variants.find((v) => v.external_id === nextSelectedVariant) || {};

      setOrderDetails({
        totalQuantity: quantity * (variant.unit_multiplier || 1),
        date: null,
        selectedTimeslot: null,
      });

      if (!exceededBookingsLimit && !exceededTrialPlan && !loading && !isHiddenVariant(variants, nextSelectedVariant)) {
        const { start, finish } = getTimeFrameFromDate(currentMonth || new Date(), timeZone);

        if (isLocationChanged) {
          resetFirstOccurrence();
        }

        fetchFrames({
          start: start.format(),
          finish: finish.format(),
          location_id: nextSelectedLocation?.id,
          resource_id: nextSelectedResource?.id,
        }).then((data) => {
          if (data.frames.length === 0 && data.first_occurrence) {
            self.onMonthChange(data.first_occurrence);
          } else {
            self.setFirstAvailableTimeslot(data.frames);
          }
        });
      }
    }

    if (!isEqual(prevTimeZone, timeZone)) {
      const condition = selectedTimeslot && !isLocationChanged;
      this.updateTimeZone(condition);
    }

    if (!isEqual(frames, prevFrames)) {
      const condition = frames.length > 0 && nextSelectedVariant;
      const updState = { availableDates: [] };

      this.updateSetup(condition, updState, true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  getCanBook() {
    const { available, frames, selectedDate, selectedTimeslot, selectedVariant, totalQuantity, variants } = this.props;
    return selectedDate && selectedTimeslot && available >= totalQuantity
      && frames.length && variants.length && selectedVariant;
  }

  getFirstBookableDate(frames, availableDates) {
    const today = new Date();
    let firstBookableFrame = first(filter(
      frames,
      (x) => dayjs(x.date).diff(today, 'days') >= 0 && this.getFirstAvailableTimeslot(x.date, frames).available > 0,
    ));

    if (!firstBookableFrame) {
      firstBookableFrame = first(filter(frames, (x) => dayjs(x.date).diff(today, 'days') >= 0));
    }

    const firstBookableDate = firstBookableFrame ? firstBookableFrame.date : null;

    if (!availableDates.includes(firstBookableDate)) {
      return availableDates[0];
    }

    return firstBookableDate;
  }

  getTimeSlots(value) {
    const { frames, selectedLocation, orderActions: { setOrderDetails } } = this.props;
    const locationId = selectedLocation ? selectedLocation.id : null;
    let frame = null;
    let timeslots = [];

    if (value) {
      frame = find(frames, (x) => x.date === value);

      if (frame) {
        timeslots = filter(
          frame.timeslots,
          (x) => dayjs(x.start).isSameOrAfter(dayjs())
            && (!locationId || !x.location_id || x.location_id === locationId),
        ).sort((x, y) => (dayjs(y.start).isSameOrAfter(dayjs(x.start)) ? -1 : 1));
      } else {
        setOrderDetails({ date: null, selectedTimeslot: null });
      }
    }

    return timeslots;
  }

  handleAddItems(reservation, callback) {
    const type = ['Date', 'Time', 'Location'];
    this.onAddItems({ reservation, type, callback });
  }

  handleDayClick(day, modifiers = {}) {
    this.onDayClick(day, modifiers, true);
  }

  handleDayMouseEnter(day) {
    const { selectedVariant, timeZone } = this.props;
    const duration = this.getDuration(selectedVariant);

    this.setState({
      hoverStart: dayjs(day).tz(timeZone).toDate(),
      hoverEnteredTo: dayjs(day).tz(timeZone).add(duration - 1, 'seconds').toDate(),
    });
  }

  handleFramesFetched(data) {
    const { timeZone, urlParams, frames, selectedLocation } = this.props;
    const availableDates = parseAvailableDates({ frames, selectedLocation, timeZone });
    this.setState({ availableDates });

    if (!dateFromParams(urlParams, timeZone)) {
      this.setFirstAvailableTimeslot(data.frames);
    }
  }

  handleMonthChange(value, callback) {
    this.onMonthChange(value, callback);
  }

  handleNextMonthClick() {
    const { nextOccurrence } = this.props;
    if (nextOccurrence) {
      this.onMonthChange(dayjs(nextOccurrence).format(SHORT_DATE_FORMAT));
    }
  }

  handleSubmit(e) {
    const {
      bookingFields,
      setting,
      orderVariants,
      orderActions: { submitClassOrder },
    } = this.props;
    const showBookingFields = stringToBoolean(setting.show_booking_fields);
    const messages = showBookingFields && bookingFields.length ? [] : this.getValidMessage();

    this.onSubmit({
      element: e,
      messages,
      itemsResponse: orderVariants,
      action: submitClassOrder,
      bookingFields,
      isShowAlert: true,
    });
  }

  onSelectTimeSlot(timeslot) {
    const { frames, orderActions: { setOrderDetails }, selectedDate } = this.props;

    if (timeslot) {
      const frame = frames.find((x) => x.date === selectedDate);
      const availability = this.getAvailability(timeslot);

      setOrderDetails({
        date: frame.date,
        available: availability,
        selectedTimeslot: timeslot,
      });
    }
  }

  renderTimeSlot(value) {
    const {
      locale,
      selectedTimeslot,
      selectedVariant,
      setting,
      style,
      timeZone,
      totalQuantity,
      variants,
    } = this.props;
    const { availableDates } = this.state;
    const t = new Translation(this.props);
    const variant = variants.find((v) => v.external_id === selectedVariant) || {};
    const timeslots = this.getTimeSlots(value);

    return (
      <TimeSlotRadioButtons
        availableDates={availableDates}
        getAvailability={this.getAvailability}
        isDisplayRange={stringToBoolean(setting.display_time_range)}
        isShowRemainingSpace={stringToBoolean(setting.show_remaining_places)}
        isShowWaitlist={this.showWaitlist()}
        locale={locale}
        onItemClick={this.onSelectTimeSlot}
        selectedDate={value}
        selectedTimeSlot={selectedTimeslot}
        style={style}
        timeFormat={setting.time_format}
        timeslots={timeslots}
        timeZone={timeZone}
        translation={t}
        quantity={totalQuantity}
        variant={variant}
      />
    );
  }

  renderTimeslots() {
    const { selectedDate } = this.props;
    const timeslots = this.getTimeSlots(selectedDate);

    if (timeslots.length) {
      return (
        <StyledWrapperRadioList>
          {this.renderTimeSlot(selectedDate)}
        </StyledWrapperRadioList>
      );
    }

    return null;
  }

  setDefaultSetup() {
    const { currentMonth } = this.state;
    const {
      frames,
      selectedDate,
      selectedLocation,
      timeZone,
      orderActions: { setOrderDetails },
    } = this.props;
    const availableDates = parseAvailableDates({ frames, selectedLocation, timeZone });

    if (!selectedDate && !currentMonth) {
      const firstBookableDate = this.getFirstBookableDate(frames, availableDates);

      if (firstBookableDate) {
        const data = this.getFirstAvailableTimeslot(firstBookableDate, frames);
        let { timeslot } = data;
        const { available } = data;

        if (!timeslot) {
          timeslot = this.getFirstAvailableTimeslot(firstBookableDate, frames, true).timeslot;
        }

        setOrderDetails({
          date: firstBookableDate,
          available,
          selectedTimeslot: timeslot,
        });

        this.setDefaultDates(firstBookableDate, availableDates);
      }
    } else {
      this.setState({ availableDates });
    }
  }

  setFirstAvailableTimeslot(frames) {
    const { orderActions: { setOrderDetails }, selectedLocation, timeZone } = this.props;
    const availableDates = parseAvailableDates({ frames, selectedLocation, timeZone });
    const firstBookableDate = this.getFirstBookableDate(frames, availableDates);

    if (firstBookableDate) {
      let { available, timeslot } = this.getFirstAvailableTimeslot(firstBookableDate, frames);

      if (!timeslot) {
        const availability = this.getFirstAvailableTimeslot(firstBookableDate, frames, true);
        available = availability.available;
        timeslot = availability.timeslot;
      }

      setOrderDetails({
        date: firstBookableDate,
        available,
        selectedTimeslot: timeslot,
      });
    }
  }

  shouldFetchFrames() {
    const { selectedVariant, variants } = this.props;
    return super.shouldFetchFrames() && !isHiddenVariant(variants, selectedVariant);
  }

  render() {
    if (this.state.bookingUpdated) {
      return <UpdatedBookingPage />;
    }

    const {
      variants,
      loading,
      locations = [],
      locale,
      selectedDate,
      selectedTimeslot,
      selectedVariant,
      selectedLocation,
      selectedResource,
      available,
      orderVariants,
      productTitle,
      style,
      setting,
      timeZone,
      orderActions,
      shopDatepickerTimeInterval,
      shopDateFormat,
      brandingVisualized,
    } = this.props;
    const { modalHeight } = this.state;
    const enableLocationDropdown = this.getEnableLocationDropdown();
    const enableResourceDropdown = this.getEnableResourceDropdown();
    const isVisibleVariant = !isHiddenVariant(variants, selectedVariant);
    const showGoNextMonthButton = this.getShowNextMoBtn();
    const canBook = this.getCanBook();
    const t = new Translation(this.props);
    const wrapperStyle = { ...style.body, minHeight: '420px', ...style.boxShadow };
    const isRenderWaitlistButton = this.showWaitlist() && selectedTimeslot && !canBook;
    const selectedTime = selectedTimeslot ? selectedTimeslot.start : null;

    return (
      <div className="text-center center widget-container" style={{ minHeight: modalHeight + 20 }}>
        <CloseModalIcon />
        <Alert ref={this.setAlertRef} />
        <div className={`sub-panel ${setting.mode}`} style={wrapperStyle}>
          <h3 className="text-center widget-title" style={{ ...style.title }}>
            {t.key('title')}
          </h3>

          {this.renderExceededAlert(true)}
          {isVisibleVariant && this.renderLocationDropdown(loading)}
          {isVisibleVariant && this.renderResourceDropdawn()}
          {this.renderVariantSelect()}
          {this.renderQuantityInput(available, 'quantityPrompt')}

          <PaymentOptions />

          {isVisibleVariant && (
            <>
              {this.renderDatePicker()}

              {showGoNextMonthButton ? this.renderGoNextMonthButton() : (selectedDate && this.renderTimeslots())}
            </>
          )}

          <StyledTimeZone timeZone={timeZone} selectedTime={selectedTime} />
          <Price style={{ ...style.body }} title={`${t.key('total')}`} />
          {this.renderWaitlist(selectedTime)}
          {isRenderWaitlistButton ? this.renderWaitlistButton() : this.renderButton(canBook, loading)}

          {this.renderModal(
            <ConfirmationPage
              alert={<Alert ref={this.setAlertRef} />}
              canBook={canBook}
              closeModal={this.closeModal ? this.closeModal : () => {}}
              dateFormat={shopDateFormat}
              enableLocationDropdown={enableLocationDropdown}
              enableResourceDropdown={enableResourceDropdown}
              getValidationFn={this.handleGetValidationFn}
              locale={locale}
              locations={locations}
              orderActions={orderActions}
              productTitle={productTitle}
              renderSubmitButton={this.renderSubmitButton}
              selectedLocation={selectedLocation}
              selectedResource={selectedResource}
              setting={setting}
              setWrapperHeight={this.setWrapperHeight}
              style={style}
              timeInterval={shopDatepickerTimeInterval}
              timeSlotSelected={selectedTimeslot}
              timeZone={timeZone}
              translation={t}
              variants={orderVariants}
            />,
            brandingVisualized
          )}
          {brandingVisualized && <BrandingString />}
        </div>
      </div>
    );
  }
}
